// apps/client/src/hooks/pipelineTracker/useStageTaxonomy.ts
//
// Pipeline Tracker: reading and editing the deal-stage taxonomy.
//
// Mapping a raw provider stage to a canonical one is a READ-TIME resolution, so
// saving a row here fixes every snapshot already captured, including ones taken
// months ago. Nothing is reprocessed and nothing stored is rewritten: the
// snapshot keeps the raw value exactly as Salestrekker emitted it, and the
// report resolves it on the way out.
//
// The server takes the WHOLE mapping array plus the version the client loaded,
// so every save here is a read-modify-write under optimistic concurrency. Two
// admins editing at once means the second save is REFUSED, not silently merged.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  TAXONOMY_CALLABLE,
  GET_TAXONOMY_ACTION,
  SAVE_MAPPINGS_ACTION,
  DEAL_STAGE_TAXONOMY,
  WORKFLOW_WILDCARD,
} from './reportContract';
import { invalidateTrackerReports } from './reportCache';

/** Mirrors CanonicalDealStageSchema. `order` is funnel position, not progress. */
export interface CanonicalStage {
  key: string;
  label: string;
  order: number;
  class: 'active' | 'lost' | 'post_settlement';
  staleAfterDays: number | null;
}

/** Mirrors TaxonomyMappingRowSchema. */
export interface MappingRow {
  raw: string;
  workflow: string;
  canonicalKey: string;
  effectiveFrom: string | null;
  note?: string;
}

interface TaxonomyDoc {
  taxonomyType: string;
  canonical: CanonicalStage[];
  mappings: MappingRow[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/**
 * Matching form of a raw value, mirroring normaliseRawValue on the server.
 *
 * Deliberately shallow: case folding, trimming and collapsing whitespace, and
 * nothing else. It must not diverge from the server's version, or the client
 * would think it is replacing a row while the server appends a second one that
 * resolves differently.
 */
export const matchKey = (raw: string): string => raw.replace(/\s+/g, ' ').trim().toLowerCase();

export interface SaveOutcome {
  ok: boolean;
  /** Set when another editor saved first; the caller should reload, not retry. */
  conflict?: boolean;
  message?: string;
}

/**
 * @param companyId  tenant whose taxonomy is being read.
 * @param enabled    false for a viewer who may not edit, so their page makes no
 *                   call at all rather than making one that will be denied.
 */
export function useStageTaxonomy(companyId: string | undefined, enabled: boolean) {
  const [doc, setDoc] = useState<TaxonomyDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const res = (await call({
        action: GET_TAXONOMY_ACTION,
        companyId,
        taxonomyType: DEAL_STAGE_TAXONOMY,
      })) as HttpsCallableResult<{ taxonomy: TaxonomyDoc }>;
      setDoc(res.data.taxonomy);
    } catch (err) {
      console.error('[PipelineTracker] getTaxonomy failed', err);
      setError('The stage list could not be loaded, so mapping is unavailable right now.');
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Canonical stages in funnel order, which is the order a picker should offer. */
  const canonical = useMemo(
    () => [...(doc?.canonical ?? [])].sort((a, b) => a.order - b.order),
    [doc]
  );

  /** What each raw value currently resolves to, keyed by matching form. */
  const mappedKeys = useMemo(() => {
    const out = new Map<string, string>();
    for (const row of doc?.mappings ?? []) out.set(matchKey(row.raw), row.canonicalKey);
    return out;
  }, [doc]);

  /**
   * Point one raw provider value at one canonical stage.
   *
   * The row is written WILDCARD-scoped, applying in every workflow. That is the
   * additive-safe default: a wildcard row is always beaten by a named-workflow
   * row, so a tenant who later needs "Deal In Progress" to mean two different
   * things in two workflows adds the narrower row without touching this one.
   *
   * `effectiveFrom` is null, meaning "has always applied", because the point of
   * mapping a value the tracker has been carrying unmapped is to recover the
   * history it was already sitting on. A dated row is for a provider RENAME,
   * where old snapshots must keep resolving the way they were captured.
   */
  const saveMapping = useCallback(
    async (raw: string, canonicalKey: string): Promise<SaveOutcome> => {
      if (!companyId || !doc) return { ok: false, message: 'Not ready.' };
      setSaving(raw);
      try {
        const key = matchKey(raw);
        const others = doc.mappings.filter(
          (row) => !(matchKey(row.raw) === key && row.workflow === WORKFLOW_WILDCARD)
        );
        const next: MappingRow[] = [
          ...others,
          { raw, workflow: WORKFLOW_WILDCARD, canonicalKey, effectiveFrom: null },
        ];

        const call = httpsCallable(functions, TAXONOMY_CALLABLE);
        const res = (await call({
          action: SAVE_MAPPINGS_ACTION,
          companyId,
          taxonomyType: DEAL_STAGE_TAXONOMY,
          mappings: next,
          expectedVersion: doc.version,
        })) as HttpsCallableResult<{ version: number }>;

        setDoc({ ...doc, mappings: next, version: res.data.version });
        invalidateTrackerReports();
        return { ok: true };
      } catch (err) {
        const code = (err as { code?: string }).code ?? '';
        if (code.endsWith('aborted')) {
          // Someone else saved between our load and our write. Retrying would
          // overwrite their edit, so the only correct move is to reload and let
          // this admin see what changed before deciding again.
          console.error('[PipelineTracker] saveMappings version conflict', err);
          void load();
          return {
            ok: false,
            conflict: true,
            message: 'Someone else changed the stage mapping while this page was open. Reloaded; check the list and try again.',
          };
        }
        console.error('[PipelineTracker] saveMappings failed', err);
        return { ok: false, message: 'That mapping could not be saved. Nothing was changed.' };
      } finally {
        setSaving(null);
      }
    },
    [companyId, doc, load]
  );

  /** Remove a wildcard mapping, returning the value to the unmapped worklist. */
  const clearMapping = useCallback(
    async (raw: string): Promise<SaveOutcome> => {
      if (!companyId || !doc) return { ok: false, message: 'Not ready.' };
      setSaving(raw);
      try {
        const key = matchKey(raw);
        const next = doc.mappings.filter(
          (row) => !(matchKey(row.raw) === key && row.workflow === WORKFLOW_WILDCARD)
        );
        const call = httpsCallable(functions, TAXONOMY_CALLABLE);
        const res = (await call({
          action: SAVE_MAPPINGS_ACTION,
          companyId,
          taxonomyType: DEAL_STAGE_TAXONOMY,
          mappings: next,
          expectedVersion: doc.version,
        })) as HttpsCallableResult<{ version: number }>;
        setDoc({ ...doc, mappings: next, version: res.data.version });
        invalidateTrackerReports();
        return { ok: true };
      } catch (err) {
        console.error('[PipelineTracker] clearMapping failed', err);
        return { ok: false, message: 'That mapping could not be removed. Nothing was changed.' };
      } finally {
        setSaving(null);
      }
    },
    [companyId, doc]
  );

  return {
    canonical,
    mappings: doc?.mappings ?? [],
    mappedKeys,
    version: doc?.version ?? null,
    ready: doc !== null,
    loading,
    error,
    /** Raw value currently being written, so one row can show a spinner. */
    saving,
    saveMapping,
    clearMapping,
    reload: load,
  };
}
